import { ChowPin } from '../assets/images';
import { CareHome } from '../types';

// Get URL parameter for pin style
export const getPinStyle = (): 'round' | 'balloon' => {
    const urlParams = new URLSearchParams(window.location.search);
    const pinStyle = urlParams.get('pinstyle');
    return pinStyle === 'balloon' ? 'balloon' : 'round';
};

// Get marker color based on freshness_days as specified in design
export const getMarkerColor = (freshness_days: number): string => {
    if (freshness_days >= 1460) return '#606163';
    if (freshness_days >= 1095) return '#614041';
    if (freshness_days >= 730) return '#791616';
    if (freshness_days >= 365) return '#8dac8e';
    if (freshness_days >= 180) return '#5fb761';
    return '#00ba1d';
};

// Get openscore color based on score - matching OpenScoreCard exactly
export const getOpenScoreColor = (score: number): { color1: string; color2: string } => {
    if (score > 8) return { color1: "#27E757", color2: "#0DAF35" };
    if (score >= 6.5) return { color1: "#86DF11", color2: "#5CAE13" };
    if (score >= 4.5) return { color1: "#F5C60A", color2: "#DA840B" };
    if (score >= 2) return { color1: "#F7774A", color2: "#DF520C" };
    return { color1: "#F5443A", color2: "#C11626" };
};

// Legacy function - keeping for compatibility with balloon style
export const getGlyphColor = (score: number): string => {
    if (score > 8) return '#27E757'; // Green for high scores
    if (score >= 6.5) return '#86DF11'; // Orange for medium scores
    if (score >= 4.5) return '#F5C60A'; // Red for low scores
    if (score >= 2) return '#F7774A'; // Red for low scores
    return '#F5443A'; // Gray for very low scores
};

// update 2026_05_12.1 - hide the value when score < 1; the pin itself still renders.
const formatPinScore = (score: number): string => (score < 1 ? '' : score.toFixed(1).replace(/\.0$/, ''));
const getScorePinSize = (score: number): number => (score < 1 ? 18 : 36);
const hiddenScorePinColors = { color1: '#a7adb4', color2: '#6f7882' };
const hiddenScoreGlyphColor = '#7d8791';

// Create a round pin with openscore coloring (NEW DEFAULT STYLE)
export const createRoundPin = (_freshness_days: number, score: number, _is_premium: boolean = false): HTMLElement => {
    // update 2026_05_12.1 - when the score is hidden (<1), keep the pin muted.
    const scoreColors = score < 1 ? hiddenScorePinColors : getOpenScoreColor(score);
    const formattedScore = formatPinScore(score);
    const size = getScorePinSize(score);
    const fontSize = formattedScore.length >= 4 ? '12px' : formattedScore.length === 3 ? '14px' : '15px';

    const wrapper = document.createElement('div');
    wrapper.style.position = 'relative';
    wrapper.style.display = 'flex';
    wrapper.style.alignItems = 'center';
    wrapper.style.justifyContent = 'center';
    wrapper.style.width = `${size}px`;
    wrapper.style.height = `${size}px`;
    wrapper.style.transformOrigin = 'center center';

    const badge = document.createElement('div');
    badge.style.display = 'flex';
    badge.style.alignItems = 'center';
    badge.style.justifyContent = 'center';
    badge.style.width = '100%';
    badge.style.height = '100%';
    badge.style.borderRadius = '999px';
    badge.style.background = `linear-gradient(160deg, ${scoreColors.color1} 0%, ${scoreColors.color2} 100%)`;
    badge.style.border = '2px solid #d9f4dc';
    badge.style.boxShadow = '0 4px 10px rgba(12, 95, 87, 0.2)';
    badge.style.color = '#ffffff';
    badge.style.fontFamily = '"Trebuchet MS", sans-serif';
    badge.style.fontSize = fontSize;
    badge.style.fontWeight = '700';
    badge.style.lineHeight = '1';
    badge.style.letterSpacing = '-0.02em';
    badge.style.textAlign = 'center';
    badge.style.whiteSpace = 'nowrap';
    badge.textContent = formattedScore;

    wrapper.appendChild(badge);

    return wrapper;
};

// Create a balloon pin (LEGACY STYLE for pinstyle=balloon)
export const createBalloonPin = (freshness_days: number, score: number, is_premium: boolean = false): HTMLElement => {
    // Create a custom pin element using the original balloon style
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    svg.setAttribute('viewBox', '0 0 24 24');

    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '12');
    circle.setAttribute('cy', '12');
    circle.setAttribute('r', '11');
    // update 2026_05_12.1 - when the score is hidden (<1), keep the glyph muted.
    circle.setAttribute('fill', score < 1 ? hiddenScoreGlyphColor : getGlyphColor(score));
    circle.setAttribute('stroke', 'white');
    circle.setAttribute('stroke-width', '2');

    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('x', '12');
    text.setAttribute('y', '16');
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('fill', 'white');
    text.setAttribute('font-size', '12');
    text.setAttribute('font-weight', 'bold');
    // update 2026_05_12.1 - hide the value when score < 1; the glyph still renders.
    text.textContent = score < 1 ? '' : score.toFixed(1).toString();

    svg.appendChild(circle);
    svg.appendChild(text);

    const color = getMarkerColor(freshness_days);
    // Use 130% scale (1.3) for premium care homes, 110% (1.1) for regular ones
    const scale = (is_premium ? 1.3 : 1.1) * (score < 1 ? 0.5 : 1);
    
    const pinElement = new google.maps.marker.PinElement({
        background: color,
        glyph: svg,
        scale: scale    
    });
    return pinElement.element;
};

// Create a CHOW (Care Home Open Week) logo pin for event-filtered results
export const createChowPin = (): HTMLElement => {
    const size = 36;
    const wrapper = document.createElement('div');
    wrapper.style.position = 'relative';
    wrapper.style.display = 'flex';
    wrapper.style.alignItems = 'center';
    wrapper.style.justifyContent = 'center';
    wrapper.style.width = `${size}px`;
    wrapper.style.height = `${size}px`;
    wrapper.style.transformOrigin = 'center center';

    const img = document.createElement('img');
    img.src = ChowPin;
    img.width = size;
    img.height = size;
    img.style.borderRadius = '999px';
    img.style.boxShadow = '0 4px 10px rgba(90, 50, 120, 0.25)';
    img.draggable = false;

    wrapper.appendChild(img);
    return wrapper;
};

// Main pin creation function that chooses style based on URL parameter
export const createPinElement = (freshness_days: number, score: number, is_premium: boolean = false, eventFilterActive: boolean = false): HTMLElement => {
    if (eventFilterActive) {
        return createChowPin();
    }

    const pinStyle = getPinStyle();
    
    if (pinStyle === 'balloon') {
        return createBalloonPin(freshness_days, score, is_premium);
    } else {
        return createRoundPin(freshness_days, score, is_premium);
    }
};


// Create custom marker SVG with score and color (legacy function - keeping for compatibility)
export const createCustomMarkerIcon = (careHome: CareHome): string => {
    const color = getMarkerColor(careHome.freshness_days);
    const size = careHome.is_premium ? 65 : 50; // 130% larger for premium
    const fontSize = careHome.is_premium ? 14 : 11;
    
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="${size}" height="${size}" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
            <circle cx="25" cy="25" r="22" fill="${color}" stroke="#ffffff" stroke-width="2"/>
            <text x="25" y="25" text-anchor="middle" dominant-baseline="central" 
                  fill="white" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="bold">
                ${careHome.open_score.toFixed(1)}
            </text>
        </svg>
    `)}`;
};

// Format freshness days into human readable text
export const formatFreshness = (days: number): string => {
    if (days < 30) return `${days} days ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
};

// Get freshness category for display
export const getFreshnessCategory = (days: number): string => {
    if (days < 180) return 'Very Fresh';
    if (days < 365) return 'Fresh';
    if (days < 730) return 'Moderate';
    if (days < 1095) return 'Old';
    if (days < 1460) return 'Very Old';
    return 'Stale';
}; 
